import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

// Small pieces of on-screen interface, sized in container units of the phone screen.

interface ScreenChipProps {
    children: ReactNode
    icon?: ReactNode
    tone?: 'dark' | 'light' | 'cyan' | 'violet'
    className?: string
}

const CHIP_TONES = {
    dark: 'bg-black/55 text-white ring-white/15',
    light: 'bg-white/90 text-slate-900 ring-black/5',
    cyan: 'bg-cyan-400/20 text-cyan-200 ring-cyan-300/40',
    violet: 'bg-violet-500/25 text-violet-100 ring-violet-300/40'
}

export const ScreenChip: FC<ScreenChipProps> = ({ children, icon, tone = 'dark', className }): ReactNode => {
    return (
        <span
            className={cn(
                'absolute z-10 inline-flex items-center gap-[0.6cqw] whitespace-nowrap rounded-full px-[1.3cqw] py-[0.55cqw] text-[1.45cqw] font-semibold leading-none ring-1 backdrop-blur-sm',
                CHIP_TONES[tone],
                className
            )}
        >
            {icon}
            {children}
        </span>
    )
}

interface ScrubberProps {
    progress: number
    className?: string
    color?: string
}

export const Scrubber: FC<ScrubberProps> = ({ progress, className, color = 'bg-white' }): ReactNode => {
    return (
        <span className={cn('absolute inset-x-[2.4cqw] bottom-[2.2cqw] z-10 h-[0.5cqw] rounded-full bg-white/30', className)}>
            <span className={cn('absolute inset-y-0 left-0 rounded-full', color)} style={{ width: `${progress}%` }} />
            <span
                className={cn('absolute top-1/2 size-[1.4cqw] -translate-x-1/2 -translate-y-1/2 rounded-full shadow', color)}
                style={{ left: `${progress}%` }}
            />
        </span>
    )
}

interface RangeTrackProps {
    from: number
    to: number
    color: string
    className?: string
}

// A trim track with a highlighted segment and two handles.
export const RangeTrack: FC<RangeTrackProps> = ({ from, to, color, className }): ReactNode => {
    return (
        <span className={cn('relative block h-[0.7cqw] rounded-full bg-white/12', className)}>
            <span className={cn('absolute inset-y-0 rounded-full', color)} style={{ left: `${from}%`, width: `${to - from}%` }} />
            <span className={cn('absolute top-1/2 h-[2.2cqw] w-[0.55cqw] -translate-x-1/2 -translate-y-1/2 rounded-full', color)} style={{ left: `${from}%` }} />
            <span className={cn('absolute top-1/2 h-[2.2cqw] w-[0.55cqw] -translate-x-1/2 -translate-y-1/2 rounded-full', color)} style={{ left: `${to}%` }} />
        </span>
    )
}

interface SliderTrackProps {
    value: number
    fill: string
    className?: string
    ticks?: number
}

export const SliderTrack: FC<SliderTrackProps> = ({ value, fill, className, ticks = 0 }): ReactNode => {
    return (
        <span className={cn('relative block h-[0.7cqw] rounded-full bg-white/12', className)}>
            <span className={cn('absolute inset-y-0 left-0 rounded-full', fill)} style={{ width: `${value}%` }} />

            {Array.from({ length: ticks }, (_, index) => (
                <span
                    key={index}
                    className='absolute top-1/2 size-[0.45cqw] -translate-x-1/2 -translate-y-1/2 rounded-full bg-white/50'
                    style={{ left: `${((index + 1) * 100) / (ticks + 1)}%` }}
                />
            ))}

            <span
                className='absolute top-1/2 h-[2.4cqw] w-[0.6cqw] -translate-x-1/2 -translate-y-1/2 rounded-full bg-white shadow-[0_0_1cqw_rgb(34_211_238/0.8)]'
                style={{ left: `${value}%` }}
            />
        </span>
    )
}

interface ScreenButtonProps {
    children: ReactNode
    className?: string
}

export const ScreenButton: FC<ScreenButtonProps> = ({ children, className }): ReactNode => {
    return (
        <span
            className={cn(
                'inline-flex items-center gap-[0.6cqw] rounded-[0.8cqw] bg-white/8 px-[1cqw] py-[0.55cqw] text-[1.25cqw] font-semibold leading-none text-white/90 ring-1 ring-white/12',
                className
            )}
        >
            {children}
        </span>
    )
}
